import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/feature/auth';
import { settingsService } from './service/settings.service';
import { managementService } from '../management/service/management.service';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { authService } from '../auth/service/auth.service';
import { queryClient } from '../app/queryClient';
import { EditEmail } from './popups';
import usePopup from '../common/feature/popup';
import { Ikey } from '../management/types.type';
import { PortalButton } from './ui';
import { DeveloperKeys } from './ui/developer-keys.component';

export const SettingsPageContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, setIsAuthenticated } = useAuth();
  const { handleModal, modal, showPopup } = usePopup();
  // `?section=developer` opens a section directly: Riser links here for keys.
  const [activeSection, setActiveSection] = React.useState<string>(
    () => new URLSearchParams(window.location.search).get('section') ?? 'account'
  );
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showCurrentPw, setShowCurrentPw] = React.useState(false);
  const [showNewPw, setShowNewPw] = React.useState(false);
  const [showConfirmPw, setShowConfirmPw] = React.useState(false);

  // Password form
  const {
    register: regPw,
    handleSubmit: handlePw,
    reset: resetPw,
    formState: { errors: pwErrors }
  } = useForm<{ password: string; newPassword: string; confirmPassword: string }>();

  const { mutate: changePassword, isPending: pwPending } = useMutation({
    mutationFn: (data: { password: string; newPassword: string }) =>
      settingsService.changePassword({
        password: data.password,
        newPassword: data.newPassword
      }),
    onSuccess: () => {
      toast.success(t('settings.passwordUpdated'));
      resetPw();
      setActiveSection('account');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      toast.error(msg || t('settings.passwordChangeFailed'));
    }
  });

  // Subscription data
  const keysQuery = useQuery({
    queryKey: ['keys'],
    queryFn: async () => managementService.getLicenseKeys()
  });

  // Licences from Squarebit's own licence server. Separate from getLicenseKeys
  // above, which returns the LicenseSpring keys and knows nothing about these,
  // so without this the membership a granted tester actually holds would show
  // nowhere in Settings. getMyLicenses swallows its own failures and returns []
  // (the endpoint 404s by design while LICENSING_ENABLED is off), so this can
  // never turn Settings into an error state.
  const myLicensesQuery = useQuery({
    queryKey: ['my-licenses'],
    queryFn: async () => managementService.getMyLicenses()
  });
  const eyeLicense = (myLicensesQuery.data ?? []).find(
    (l) => l.productSlug === 'squarebit-eye'
  );

  const hasPortalQuery = useQuery({
    queryKey: ['portal'],
    queryFn: async () => settingsService.getUserHasPortal()
  });

  // Admin-granted entitlements (invite-only products). Shares the query key
  // with My Products so the fetch is cached across both pages.
  const accessQuery = useQuery({
    queryKey: ['my-access'],
    queryFn: async () => managementService.getMyAccess()
  });
  const hasEyeAccess = !!accessQuery.data?.includes('squarebit-eye');
  // Developer API keys are for the products that have an API, and only for
  // an account holding that product's grant.
  const hasDeveloperAccess = !!accessQuery.data?.includes('riser');

  // Delete account
  const { mutate: deleteAccount, isPending: deletePending } = useMutation({
    mutationFn: () => settingsService.deleteAccount(),
    onSuccess: async () => {
      toast.success(t('settings.accountDeleted'));
      await authService.logout();
      setIsAuthenticated(false);
      queryClient.clear();
      navigate(ROUTER_LINKS.ROOT);
    },
    onError: () => {
      toast.error(t('settings.accountDeleteFailed'));
    }
  });

  const onPasswordSubmit = (data: {
    password: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }
    changePassword({ password: data.password, newPassword: data.newPassword });
  };

  // Derive subscription info from license keys — every key is its own plan
  // (a user can hold subscriptions to several products at once).
  const licenseKeys: Ikey[] = ((keysQuery.data as Ikey[] | undefined) ?? [])
    .slice()
    .sort((a, b) => new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime());

  const hasSubscription = licenseKeys.length > 0;
  const showManageButton = !!(hasPortalQuery.isSuccess && hasPortalQuery.data);

  const navItems = [
    { key: 'account', label: t('settings.account'), icon: accountIcon },
    { key: 'password', label: t('settings.password'), icon: lockIcon },
    { key: 'subscription', label: t('settings.subscription'), icon: cardIcon },
    ...(hasDeveloperAccess
      ? [{ key: 'developer', label: t('settings.developer.nav'), icon: codeIcon }]
      : []),
    { key: 'danger', label: t('settings.deleteAccount'), icon: trashIcon }
  ];

  if (showPopup) return <>{modal}</>;

  return (
    <div className="w-full max-w-[700px] mx-auto py-[40px] px-[16px] self-start">
      <h1 className="font-display text-[28px] font-bold text-base-black mb-[32px]">
        {t('settings.title')}
      </h1>

      <div className="bg-surface-base rounded-[16px] border border-border-dark/[0.11] shadow-[0_1px_3px_rgba(0,0,0,0.07)] overflow-hidden">
        <div className="flex flex-col md:flex-row h-[460px]">
          {/* Nav sidebar */}
          <nav className="md:w-[200px] shrink-0 border-b md:border-b-0 md:border-r border-border-dark/[0.11] flex md:flex-col">
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveSection(item.key)}
                className={`flex flex-col md:flex-row items-center gap-[3px] md:gap-[10px] px-[8px] md:px-[20px] py-[10px] md:py-[14px] font-body font-medium transition-all text-center md:text-left w-full md:border-l-[3px]
                  ${
                    activeSection === item.key
                      ? 'bg-surface-hover text-base-black md:border-blue-900'
                      : 'text-content-muted hover:bg-surface-card hover:text-base-black md:border-transparent'
                  }
                  ${item.key === 'danger' ? 'md:mt-auto' : ''}
                `}
              >
                <span className="w-[18px] h-[18px] flex items-center justify-center shrink-0">
                  {item.icon}
                </span>
                <span className="text-[10px] md:text-[14px] leading-tight">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="flex-1 p-[24px] overflow-y-auto">
            {/* Account */}
            {activeSection === 'account' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black mb-[16px]">
                  {t('settings.account')}
                </h2>
                <div className="flex items-center gap-[12px] p-[16px] rounded-[12px] bg-surface-card border border-border-dark/[0.07] mb-[16px]">
                  <span className="flex items-center justify-center w-[40px] h-[40px] rounded-full bg-blue-500 text-blue-deep-2 text-[14px] font-semibold shrink-0">
                    {(user?.email ?? '').slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="font-body text-[15px] font-semibold text-base-black truncate">
                      {user?.email?.split('@')[0]}
                    </p>
                    <p className="font-body text-[13px] text-content-muted truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleModal(EditEmail)}
                  className="h-[40px] px-[20px] rounded-[32px] bg-base-black text-surface-base font-body text-[13px] font-medium hover:bg-btn-black-hover transition-all"
                >
                  {t('settings.changeEmail')}
                </button>
              </div>
            )}

            {/* Password */}
            {activeSection === 'password' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black mb-[16px]">
                  {t('settings.changePassword')}
                </h2>
                <form
                  onSubmit={handlePw(onPasswordSubmit)}
                  className="flex flex-col gap-[12px] max-w-[360px]"
                >
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black mb-[4px] block ml-[2px]">
                      {t('settings.currentPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPw ? 'text' : 'password'}
                        placeholder={t('auth.enterHere')}
                        {...regPw('password', {
                          required: t('settings.currentPassword')
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black bg-surface-base border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-gray-700 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                      />
                      <EyeToggle
                        show={showCurrentPw}
                        onToggle={() => setShowCurrentPw(!showCurrentPw)}
                      />
                    </div>
                    {pwErrors.password && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.password.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black mb-[4px] block ml-[2px]">
                      {t('settings.newPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPw ? 'text' : 'password'}
                        placeholder={t('auth.enterHere')}
                        {...regPw('newPassword', {
                          required: t('settings.newPassword'),
                          minLength: {
                            value: 8,
                            message: t('auth.passwordValidation.minLength')
                          }
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black bg-surface-base border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-gray-700 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                      />
                      <EyeToggle
                        show={showNewPw}
                        onToggle={() => setShowNewPw(!showNewPw)}
                      />
                    </div>
                    {pwErrors.newPassword && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-[13px] font-medium text-base-black mb-[4px] block ml-[2px]">
                      {t('settings.confirmPassword')}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPw ? 'text' : 'password'}
                        placeholder={t('auth.enterHere')}
                        {...regPw('confirmPassword', {
                          required: t('settings.confirmPassword')
                        })}
                        className="w-full h-[44px] px-[16px] pr-[44px] text-[14px] font-body text-base-black bg-surface-base border border-gray-700 rounded-[24px] placeholder-gray-900 focus:outline-none focus:border-gray-700 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] transition-all"
                      />
                      <EyeToggle
                        show={showConfirmPw}
                        onToggle={() => setShowConfirmPw(!showConfirmPw)}
                      />
                    </div>
                    {pwErrors.confirmPassword && (
                      <p className="text-red-500 text-[12px] font-body mt-[4px] ml-[6px]">
                        {pwErrors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={pwPending}
                    className={`h-[44px] rounded-[32px] font-body text-[14px] font-medium text-surface-base transition-all mt-[4px] ${
                      pwPending
                        ? 'bg-gray-900 opacity-50'
                        : 'bg-base-black hover:bg-btn-black-hover'
                    }`}
                  >
                    {pwPending
                      ? t('settings.updating')
                      : t('settings.updatePassword')}
                  </button>
                </form>
              </div>
            )}

            {/* Subscription */}
            {activeSection === 'subscription' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-base-black mb-[16px]">
                  {t('settings.subscription')}
                </h2>

                {/* Plan info cards — one per active license key */}
                {licenseKeys.map((planKey) => (
                  <div
                    key={planKey.springId ?? planKey.key}
                    className={`p-[16px] rounded-[12px] mb-[16px] ${
                      planKey.type === 'trial'
                        ? 'bg-surface border border-border-brand'
                        : planKey.type === 'annual'
                          ? 'bg-gradient-to-br from-surface to-surface border border-brand'
                          : 'bg-surface border border-blue-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-[6px]">
                      <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-content-muted">
                        {t('settings.currentPlan')}
                      </p>
                      {planKey.type === 'trial' ? (
                        <span className="text-[10px] font-bold tracking-[0.06em] uppercase px-[10px] py-[3px] rounded-full bg-surface-hover text-blue-link border border-border-brand">
                          {t('settings.trial')}
                        </span>
                      ) : planKey.type === 'annual' ? (
                        <span className="text-[10px] font-bold tracking-[0.06em] uppercase px-[10px] py-[3px] rounded-full bg-blue text-surface-base">
                          {t('settings.annual')}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold tracking-[0.06em] uppercase px-[10px] py-[3px] rounded-full bg-content-primary-alt text-surface-base">
                          {t('settings.monthly')}
                        </span>
                      )}
                    </div>
                    <p
                      className={`font-display text-[22px] font-bold tracking-[-0.02em] leading-[1.2] ${
                        planKey.type === 'trial'
                          ? 'text-blue-link'
                          : planKey.type === 'annual'
                            ? 'text-blue-deep-1'
                            : 'text-base-black'
                      }`}
                    >
                      {`${planKey.productName} ${planKey.type === 'trial' ? t('settings.freeTrial') : planKey.type === 'annual' ? t('settings.annual') : t('settings.monthly')}`}
                    </p>

                    {planKey.type === 'trial' && planKey.expiresAt && (
                      <p className="font-body text-[13px] text-blue-link mt-[6px] flex items-center gap-[6px]">
                        <span className="w-[6px] h-[6px] rounded-full bg-blue animate-pulse inline-block" />
                        {(() => {
                          const days = Math.max(
                            0,
                            Math.round(
                              (new Date(planKey.expiresAt).getTime() - Date.now()) /
                                (1000 * 60 * 60 * 24)
                            )
                          );
                          return `${days} ${t('settings.daysRemaining')} · ${t('settings.ends')} ${new Date(planKey.expiresAt).toLocaleDateString()}`;
                        })()}
                      </p>
                    )}
                    {planKey.type !== 'trial' && planKey.expiresAt && (
                      <p className="font-body text-[13px] text-blue mt-[6px]">
                        {t('settings.renews')}{' '}
                        {new Date(planKey.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
                {/* Squarebit Eye entitlement — an admin-granted, currently free
                    product presented like any other plan. Structured as its own
                    entitlement row so a future paid license/subscription card
                    can simply replace it. */}
                {hasEyeAccess && (
                  <div className="p-[16px] rounded-[12px] mb-[16px] bg-surface border border-blue-500">
                    <div className="flex items-center justify-between mb-[6px]">
                      <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-content-muted">
                        {t('settings.currentPlan')}
                      </p>
                      {/* The licence's own status is the truer badge when there
                          is a licence: a revoked or lapsed one still carries the
                          access grant, so "Free" alone would read as working. */}
                      <span
                        className={`text-[10px] font-bold tracking-[0.06em] uppercase px-[10px] py-[3px] rounded-full border ${
                          eyeLicense && eyeLicense.status !== 'active'
                            ? 'bg-badge-red-bg/60 border-badge-red-text/30 text-badge-red-text'
                            : 'bg-surface-secondary-alt border-transparent text-content-muted'
                        }`}
                      >
                        {eyeLicense && eyeLicense.status !== 'active'
                          ? t(`management.eye.license.status.${eyeLicense.status}`, {
                              defaultValue: eyeLicense.status
                            })
                          : t('settings.free')}
                      </span>
                    </div>
                    <p className="font-display text-[22px] font-bold tracking-[-0.02em] leading-[1.2] text-base-black">
                      {`Squarebit Eye ${t('settings.free')}`}
                    </p>
                    {eyeLicense ? (
                      <>
                        <p className="font-body text-[13px] text-content-muted mt-[6px]">
                          {t('management.eye.license.seats', {
                            used: eyeLicense.usedSeats,
                            total: eyeLicense.seats
                          })}
                        </p>
                        <p className="font-body text-[13px] text-content-muted mt-[4px]">
                          {eyeLicense.expiresAt
                            ? `${t('settings.ends')} ${new Date(eyeLicense.expiresAt).toLocaleDateString()}`
                            : t('management.eye.license.perpetual', {
                                defaultValue: 'Perpetual'
                              })}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(ROUTER_LINKS.MANAGEMENT)}
                          className="mt-[10px] font-body text-[13px] text-blue-link hover:underline"
                        >
                          {t('settings.viewPlans')}
                        </button>
                      </>
                    ) : (
                      <p className="font-body text-[13px] text-content-muted mt-[6px]">
                        {t('management.eye.grantedBy')}
                      </p>
                    )}
                  </div>
                )}
                {!hasSubscription && !hasEyeAccess && (
                  <div className="p-[16px] rounded-[12px] mb-[16px] bg-surface-card border border-border-dark/[0.07]">
                    <div className="flex items-center justify-between mb-[6px]">
                      <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-content-muted">
                        {t('settings.currentPlan')}
                      </p>
                      <span className="text-[10px] font-bold tracking-[0.06em] uppercase px-[10px] py-[3px] rounded-full bg-surface-secondary-alt text-content-muted">
                        {t('settings.free')}
                      </span>
                    </div>
                    <p className="font-display text-[22px] font-bold tracking-[-0.02em] leading-[1.2] text-content-muted">
                      {t('settings.noSubscription')}
                    </p>
                    <p className="font-body text-[13px] text-content-muted mt-[6px]">
                      {t('settings.noSubscriptionDesc')}
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-[8px] flex-wrap">
                  {hasSubscription && (
                    <PortalButton />
                  )}
                  {!hasSubscription && (
                    <button
                      type="button"
                      onClick={() => navigate(ROUTER_LINKS.MANAGEMENT)}
                      className="h-[44px] px-[24px] rounded-[32px] bg-base-black text-surface-base font-body text-[14px] font-medium hover:bg-btn-black-hover transition-all"
                    >
                      {t('settings.upgradePlan')}
                    </button>
                  )}
                  {hasSubscription && (
                    <button
                      type="button"
                      onClick={() => navigate(ROUTER_LINKS.MANAGEMENT)}
                      className="h-[44px] px-[24px] rounded-[32px] bg-base-black text-surface-base font-body text-[14px] font-medium hover:bg-btn-black-hover transition-all"
                    >
                      {t('settings.viewPlans')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Developer API keys */}
            {activeSection === 'developer' && hasDeveloperAccess && (
              <DeveloperKeys product="riser" />
            )}

            {/* Delete Account */}
            {activeSection === 'danger' && (
              <div>
                <h2 className="font-body text-[18px] font-semibold text-red-500 mb-[8px]">
                  {t('settings.deleteAccount')}
                </h2>
                <p className="font-body text-[14px] text-gray-900 mb-[16px]">
                  {t('settings.deleteWarning')}
                </p>
                {!confirmDelete ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    className="h-[44px] px-[24px] rounded-[32px] border border-red-500 text-red-500 font-body text-[14px] font-medium hover:bg-red-500 hover:text-surface-base transition-all"
                  >
                    {t('settings.deleteMyAccount')}
                  </button>
                ) : (
                  <div className="p-[16px] rounded-[12px] border border-red-500 bg-badge-red-bg">
                    <p className="font-body text-[14px] font-semibold text-badge-red-text mb-[12px]">
                      {t('settings.deleteConfirm')}
                    </p>
                    <div className="flex gap-[8px]">
                      <button
                        type="button"
                        onClick={() => deleteAccount()}
                        disabled={deletePending}
                        className={`h-[40px] px-[20px] rounded-[32px] font-body text-[13px] font-medium text-surface-base transition-all ${
                          deletePending
                            ? 'bg-gray-900 opacity-50'
                            : 'bg-red-500 hover:bg-red-600'
                        }`}
                      >
                        {deletePending
                          ? t('settings.deleting')
                          : t('settings.deleteConfirmButton')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        className="h-[40px] px-[20px] rounded-[32px] border border-gray-500 bg-surface-base font-body text-[13px] font-medium text-base-black hover:bg-surface-secondary transition-all"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Inline helpers ---

const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="absolute right-[14px] top-1/2 -translate-y-1/2 text-content-muted hover:text-base-black transition-colors"
  >
    {show ? (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )}
  </button>
);

const accountIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const lockIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const cardIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const codeIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const trashIcon = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
